import React from 'react';
import { useFormField } from '../../useFormField';
import type { EnumerationViewModel } from '@generated';
import type { StripControlledProperties } from '../../types';
import { SelectField, type SelectFieldProps } from '../base-fields/SelectField';

export type SelectOption = string | EnumerationViewModel | { id: string | number; name: string; description?: string };

export interface SelectFormFieldProps extends Omit<StripControlledProperties<SelectFieldProps>, 'optionsLength' | 'items'> {
	name: string;
	options: SelectOption[];
	canBeCleared?: boolean;
	onChangePassThrough?: (selected: string | null) => void;
}

// Normalize options to a consistent format for React Aria's items prop
function normalizeOption(option: SelectOption): { id: string; name: string; description?: string } {
	if (typeof option === 'string') {
		return { id: option, name: option };
	} else if ('id' in option && 'name' in option) {
		return { id: option.id.toString(), name: option.name, description: 'description' in option ? option.description : undefined };
	}
	return { id: option.id.toString(), name: (option as { id: string | number; label: string }).label };
}

export const SelectFormField = React.memo(function FormSelectFieldComponent(props: SelectFormFieldProps) {
	const fieldProps = useFormField<string | null>({ ...props, validateOnChange: true });

	const { onChangePassThrough } = props;

	const handleChange = React.useCallback(
		(value: string | number | null) => {
			const selectedValue = value ? value.toString() : null;
			fieldProps.onChange(selectedValue);
			onChangePassThrough?.(selectedValue);
		},
		[fieldProps, onChangePassThrough]
	);
	// Normalize options for React Aria's items prop
	const items = React.useMemo(() => props.options.map(normalizeOption), [props.options]);

	return (
		<SelectField key={props.name} {...props} {...fieldProps} value={fieldProps.value ?? null} onChange={handleChange} items={items}>
			{props.canBeCleared && fieldProps.value && !fieldProps.isReadOnly && (
				<button type="button" className="mt-2 text-sm text-blue-600 hover:text-blue-800" onClick={() => fieldProps.onReset()}>
					Reset
				</button>
			)}
		</SelectField>
	);
});
